import { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { doc } from 'firebase/firestore';
import { Wrench } from 'lucide-react';
import type { GlobalSettingsDoc } from '@asm/shared';
import { MAINTENANCE_DEFAULT_MESSAGE } from '@asm/shared';
import { hasSearchAiAccess } from '@asm/shared';
import { useSession } from './AuthProvider';
import { db } from '@/lib/firebase';
import { useLiveDoc } from '@/lib/hooks';
import { Spinner } from '@/components/ui/Skeleton';
import { AppMark } from '@/components/AppMark';
import { Button } from '@/components/ui/Button';

/** Shown to everyone except admins while the workspace is intentionally down. */
function MaintenanceScreen({ message }: { message?: string }) {
  const { signOut } = useSession();
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 px-6">
      <AppMark hero />
      <div className="flex max-w-md flex-col items-center text-center">
        <span className="mb-3 flex size-10 items-center justify-center rounded-full bg-warning/15">
          <Wrench className="size-5 text-warning" />
        </span>
        <h1 className="text-[15px] font-semibold">Update in progress</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
          {message?.trim() || MAINTENANCE_DEFAULT_MESSAGE}
        </p>
        <p className="mt-2 text-[12px] text-muted-foreground">
          Your work is safe — this page reopens by itself the moment an admin brings the workspace back.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={() => window.location.reload()}>Check again</Button>
        <Button variant="outline" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

/** Persistent reminder for admins that everyone else is currently locked out. */
function MaintenanceBanner() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 bg-warning/15 px-4 py-1.5 text-center text-[12px] font-medium text-foreground">
      <Wrench className="size-3.5 shrink-0 text-warning" />
      <span>Workspace is down for maintenance — only admins can use it right now.</span>
      <a href="/admin/settings" className="underline underline-offset-2">
        Bring it back online
      </a>
    </div>
  );
}

/** Live maintenance state; settings/global is readable by every active user. */
export function useMaintenance() {
  const ref = useMemo(() => doc(db, 'settings', 'global'), []);
  const settings = useLiveDoc<GlobalSettingsDoc>(ref);
  return settings.data?.maintenance ?? null;
}

function FullScreenLoader() {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-6">
      <AppMark hero />
      <Spinner />
    </div>
  );
}

/** Signed in but the profile never loaded — always offer a way forward. */
function ProfileErrorScreen() {
  const { error, signOut } = useSession();
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 px-6">
      <AppMark hero />
      <div className="max-w-sm text-center">
        <h1 className="text-[15px] font-semibold">Couldn’t open your workspace</h1>
        <p className="mt-2 text-[13px] text-muted-foreground">
          {error ?? 'Your profile could not be loaded.'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={() => window.location.reload()}>Try again</Button>
        <Button variant="outline" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

/** Wraps everything that needs an active, provisioned session. */
export function RequireAuth() {
  const { status, user } = useSession();
  const location = useLocation();
  const maintenance = useMaintenance();

  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'profileError') return <ProfileErrorScreen />;
  if (status === 'unprovisioned' || status === 'domainBlocked') return <Navigate to="/request-access" replace />;
  if (status !== 'ready') return <Navigate to="/login" replace state={{ from: location }} />;
  // Admins stay in so they can land the update and verify it before reopening.
  if (maintenance?.enabled && user?.role !== 'admin') return <MaintenanceScreen message={maintenance.message} />;
  return (
    <>
      {maintenance?.enabled && <MaintenanceBanner />}
      <Outlet />
    </>
  );
}

/** Admin-only subtree. */
export function RequireAdmin() {
  const { status, user } = useSession();
  if (status === 'profileError') return <ProfileErrorScreen />;
  if (status !== 'ready') return <FullScreenLoader />;
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return <Outlet />;
}

export function RequireSearchAi() {
  const { status, user } = useSession();
  if (status !== 'ready') return <FullScreenLoader />;
  if (!user || !hasSearchAiAccess(user)) return <Navigate to="/" replace />;
  return <Outlet />;
}
